import React from "react";
import { Popover as MuiPopover, PopoverProps as MuiPopoverProps } from "@mui/material";
import { styled } from "@mui/material/styles";

interface StyledPopoverProps {
  position: "top" | "bottom";
}

const StyledPopover = styled(MuiPopover, {
  shouldForwardProp: (prop) => prop !== "position",
})<StyledPopoverProps>(({ position }) => ({
  "& .MuiPopover-paper": {
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
    overflow: "visible",
    ...(position === "top"
      ? {
          marginTop: "-20px",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "12px solid transparent",
            borderRight: "12px solid transparent",
            borderTop: "12px solid #FFFFFF",
            filter: "drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))",
          },
        }
      : {
          marginTop: "20px",
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "12px solid transparent",
            borderRight: "12px solid transparent",
            borderBottom: "12px solid #FFFFFF",
            filter: "drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1))",
          },
        }),
  },
}));

export interface PopoverProps extends Omit<MuiPopoverProps, "open"> {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  /** Position of popover relative to anchor. Default: "top" */
  position?: "top" | "bottom";
}

const Popover: React.FC<PopoverProps> = ({
  open,
  anchorEl,
  onClose,
  children,
  position = "top",
  ...rest
}) => {
  const anchorOrigin =
    position === "top"
      ? { vertical: "top" as const, horizontal: "center" as const }
      : { vertical: "bottom" as const, horizontal: "center" as const };

  const transformOrigin =
    position === "top"
      ? { vertical: "bottom" as const, horizontal: "center" as const }
      : { vertical: "top" as const, horizontal: "center" as const };

  return (
    <StyledPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      position={position}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      {...rest}
    >
      {children}
    </StyledPopover>
  );
};

export default Popover;

